/**
 * Waste types from the European Waste Catalogue (EWC) with Norwegian names.
 *
 * Based on the European Waste Catalogue (EWC) as defined in Commission Decision
 * 2000/532/EC, as amended by Commission Decision 2014/955/EU.
 *
 * In the official EWC, hazardous waste entries are marked with an asterisk (*).
 * The two maps below split them into separate lists so each can be used as an
 * independent select-source in the VSME B7 waste questions.
 *
 * Norwegian names follow the conventions used in the Norwegian waste regulation
 * (Avfallsforskriften), which implements the EWC in Norway.
 *
 * These are curated subsets of the most common waste types relevant for
 * Norwegian SMBs. The full EWC contains several hundred entries.
 *
 * To update or extend these lists:
 *   1. Consult the official EWC list in Commission Decision 2014/955/EU:
 *      https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:02000D0532-20150601
 *   2. For official Norwegian names, refer to:
 *      - Avfallsforskriften vedlegg 1 (Waste Regulation appendix 1)
 *      - Miljødirektoratets avfallskode-register
 *      - https://www.miljodirektoratet.no/ansvarsomrader/avfall/avfallstyper/
 *   3. Codes marked with * in the EWC are hazardous → add to hazardousWasteTypesNb.
 *      Codes without * are non-hazardous → add to nonHazardousWasteTypesNb.
 *   4. Codes use the format "XXYYZZ" (6 digits, no spaces) to avoid special
 *      characters in datapoint keys.
 */

// ---------------------------------------------------------------------------
// Hazardous waste types (EWC entries marked with *)
// ---------------------------------------------------------------------------

/** EWC hazardous waste codes mapped to Norwegian waste type names. */
export const hazardousWasteTypesNb: Record<string, string> = {
  // 06 – Avfall fra uorganiske kjemiske prosesser
  "060101": "Svovelsyre og svovelig syre",
  "060102": "Saltsyre",
  "060106": "Andre syrer",

  // 07 – Avfall fra organiske kjemiske prosesser
  "070103": "Organiske halogenerte løsemidler, vaskevæsker og morlut",
  "070104": "Andre organiske løsemidler, vaskevæsker og morlut",

  // 08 – Avfall fra produksjon, formulering, levering og bruk av maling, lakk og lignende
  "080111": "Avfall av maling og lakk som inneholder organiske løsemidler eller andre farlige stoffer",
  "080117": "Avfall fra fjerning av maling eller lakk som inneholder organiske løsemidler eller andre farlige stoffer",
  "080121": "Avfall av malings- eller lakkfjerner",

  // 13 – Oljeavfall og avfall av flytende brensel
  "130205": "Mineralbaserte ikke-klorerte motor-, gir- og smøreoljer",
  "130206": "Syntetiske motor-, gir- og smøreoljer",
  "130208": "Andre motor-, gir- og smøreoljer",
  "130502": "Slam fra olje-/vannseparatorer",
  "130503": "Slam fra oljefangere",
  "130701": "Fyringsolje og diesel",
  "130702": "Bensin",

  // 14 – Avfall av organiske løsemidler, kjølemidler og drivmidler
  "140603": "Andre løsemidler og løsemiddelblandinger",

  // 15 – Avfallsemballasje; absorbenter, tørkekluter, filtreringsmaterialer og verneklær
  "150110": "Emballasje som inneholder rester av eller er forurenset av farlige stoffer",
  "150202": "Absorbenter, filtreringsmaterialer, tørkekluter og verneklær forurenset av farlige stoffer",

  // 16 – Avfall som ikke er oppført andre steder i listen
  "160107": "Oljefiltre",
  "160209": "Transformatorer og kondensatorer som inneholder PCB",
  "160213": "Kassert utstyr som inneholder farlige komponenter",
  "160601": "Blybatterier",
  "160602": "Ni-Cd-batterier",
  "160603": "Batterier som inneholder kvikksølv",

  // 17 – Bygge- og rivningsavfall
  "170106":
    "Blandinger av eller separate fraksjoner av betong, murstein, fliser og keramikk som inneholder farlige stoffer",
  "170204": "Glass, plast og tre som inneholder eller er forurenset av farlige stoffer",
  "170301": "Asfaltblandinger som inneholder kulltjære",
  "170503": "Jord og stein som inneholder farlige stoffer",
  "170601": "Isoleringsmaterialer som inneholder asbest",
  "170603": "Annet isoleringsmateriale som inneholder farlige stoffer",
  "170605": "Byggematerialer som inneholder asbest",
  "170903": "Annet bygge- og rivningsavfall som inneholder farlige stoffer",

  // 18 – Avfall fra medisinsk eller veterinær virksomhet
  "180103": "Avfall med krav til innsamling og disponering for å forebygge infeksjon",
  "180106": "Kjemikalier som består av eller inneholder farlige stoffer",
  "180108": "Cytostatika og cytotoksiske legemidler",

  // 20 – Husholdningsavfall og lignende avfall fra handel, industri og institusjoner
  "200113": "Løsemidler",
  "200114": "Syrer",
  "200115": "Baser",
  "200117": "Fotokjemikalier",
  "200119": "Pesticider",
  "200121": "Fluorescerende lysrør og annet kvikksølvholdig avfall",
  "200127": "Maling, trykkfarger, lim og harpikser som inneholder farlige stoffer",
  "200129": "Vaskemidler som inneholder farlige stoffer",
  "200133": "Batterier og akkumulatorer som er farlig avfall",
  "200135": "Kassert elektrisk og elektronisk utstyr (farlig)",
  "200137": "Tre som inneholder farlige stoffer",
}

// ---------------------------------------------------------------------------
// Non-hazardous waste types (EWC entries without *)
// ---------------------------------------------------------------------------

/** EWC non-hazardous waste codes mapped to Norwegian waste type names. */
export const nonHazardousWasteTypesNb: Record<string, string> = {
  // 02 – Avfall fra jordbruk, hagebruk, akvakultur, skogbruk, jakt og fiske
  "020103": "Avfall fra plantemateriale",
  "020104": "Plastavfall (jordbruk)",
  "020110": "Metallavfall (jordbruk)",

  // 03 – Avfall fra treindustri
  "030101": "Bark- og korkavfall",
  "030105": "Sagflis, spon, kuttings, tre, sponplater og finerplater",

  // 07 – Avfall fra organisk-kjemiske prosesser
  "070213": "Plastavfall (kjemisk industri)",

  // 08 – Avfall fra produksjon, formulering, levering og bruk av overflatebehandlingsmidler
  "080112": "Avfall av maling og lakk som ikke inneholder organiske løsemidler eller andre farlige stoffer",
  "080118":
    "Avfall fra fjerning av maling eller lakk som ikke inneholder organiske løsemidler eller andre farlige stoffer",

  // 15 – Emballasjeavfall, absorbenter, tørkekluter, filtreringsmaterialer og verneklær
  "150101": "Emballasje av papir og papp",
  "150102": "Emballasje av plast",
  "150103": "Emballasje av tre",
  "150104": "Emballasje av metall",
  "150105": "Emballasje av komposittmaterialer",
  "150106": "Blandet emballasje",
  "150107": "Emballasje av glass",
  "150109": "Emballasje av tekstiler",

  // 16 – Avfall som ikke er spesifisert andre steder i listen
  "160103": "Kasserte dekk",
  "160604": "Alkalinske batterier (ikke-farlige)",
  "160605": "Andre batterier og akkumulatorer",

  // 17 – Bygge- og rivningsavfall
  "170101": "Betong",
  "170102": "Murstein",
  "170103": "Fliser og keramikk",
  "170107": "Blandinger av betong, murstein, fliser og keramikk som ikke inneholder farlige stoffer",
  "170201": "Tre",
  "170202": "Glass",
  "170203": "Plast",
  "170302": "Bituminøse masser som ikke inneholder kullstjære",
  "170401": "Kobber, bronse, messing",
  "170402": "Aluminium",
  "170403": "Bly",
  "170404": "Sink",
  "170405": "Jern og stål",
  "170406": "Tinn",
  "170407": "Blandede metaller",
  "170411": "Kabler som ikke inneholder farlige stoffer",
  "170504": "Jord og stein som ikke inneholder farlige stoffer",
  "170506": "Muddermasser som ikke inneholder farlige stoffer",
  "170508": "Ballast fra jernbanespor som ikke inneholder farlige stoffer",
  "170604": "Isoleringsmateriale som ikke inneholder asbest eller farlige stoffer",
  "170802": "Gipsbaserte bygningsmaterialer som ikke inneholder farlige stoffer",
  "170904": "Blandet bygge- og rivningsavfall som ikke inneholder farlige stoffer",

  // 19 – Avfall fra avfallsbehandlingsanlegg
  "191202": "Jernholdige metaller (fra mekanisk behandling av avfall)",
  "191203": "Ikke-jernholdige metaller (fra mekanisk behandling av avfall)",
  "191204": "Plast og gummi (fra mekanisk behandling av avfall)",
  "191205": "Glass (fra mekanisk behandling av avfall)",
  "191207": "Tre som ikke inneholder farlige stoffer (fra mekanisk behandling)",
  "191212": "Annet avfall fra mekanisk behandling (inkl. blanding av materialer)",

  // 20 – Kommunalt avfall (husholdningsavfall og lignende avfall)
  "200101": "Papir og papp",
  "200102": "Glass",
  "200108": "Biologisk nedbrytbart kjøkken- og kantineavfall",
  "200110": "Klær",
  "200111": "Tekstiler",
  "200125": "Spiseolje og -fett",
  "200136": "Kassert elektrisk og elektronisk utstyr (ikke-farlig)",
  "200138": "Tre som ikke inneholder farlige stoffer",
  "200139": "Plast",
  "200140": "Metaller",
  "200201": "Biologisk nedbrytbart hageavfall",
  "200202": "Jord og stein",
  "200301": "Blandet kommunalt avfall (restavfall)",
  "200303": "Gateoppsop",
  "200307": "Grovavfall",
}

// ---------------------------------------------------------------------------
// Dropdown option arrays (code-sorted, with formatted EWC code prefix)
// ---------------------------------------------------------------------------

/** Helper to format a 6-digit EWC code as "XX YY ZZ" for display. */
const formatEwcCode = (code: string) => code.replace(/(\d{2})(\d{2})(\d{2})/, "$1 $2 $3")

/** Sorted array of { code, name } for use in the hazardous waste dropdown. */
export const hazardousWasteTypeOptionsNb: Array<{ code: string; name: string }> = Object.entries(hazardousWasteTypesNb)
  .map(([code, name]) => ({ code, name: `${formatEwcCode(code)} – ${name}` }))
  .sort((a, b) => a.code.localeCompare(b.code))

/** Sorted array of { code, name } for use in the non-hazardous waste dropdown. */
export const nonHazardousWasteTypeOptionsNb: Array<{ code: string; name: string }> = Object.entries(
  nonHazardousWasteTypesNb,
)
  .map(([code, name]) => ({ code, name: `${formatEwcCode(code)} – ${name}` }))
  .sort((a, b) => a.code.localeCompare(b.code))
